import Axios from 'axios'

const state = {
  topics: [],
  topic_detail: {},
  topic_subscribers: [],
  categories: [],
  loading: false,
  error: null,
  host: process.env.VUE_APP_API_HOST || 'http://localhost:8020'
}

const getters = {
  getAllTopics: state => state.topics,
  getTopicDetail: state => state.topic_detail,
  getTopicSubscribers: state => state.topic_subscribers,
  getAllCategories: state => state.categories,
  isLoading: state => state.loading,
  getError: state => state.error
}

const mutations = {
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_TOPICS(state, topics) {
    state.topics = topics
  },
  SET_TOPIC_DETAIL(state, topic) {
    state.topic_detail = topic
  },
  SET_TOPIC_SUBSCRIBERS(state, subscribers) {
    state.topic_subscribers = subscribers
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories
  },
  ADD_TOPIC(state, topic) {
    state.topics.unshift(topic)
  },
  UPDATE_TOPIC(state, updatedTopic) {
    const index = state.topics.findIndex(t => t.id === updatedTopic.id)
    if (index !== -1) {
      state.topics.splice(index, 1, updatedTopic)
    }
  },
  DELETE_TOPIC(state, id) {
    state.topics = state.topics.filter(t => t.id !== id)
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  }
}

const actions = {
  // Get all topics (admin view)
  async getAllTopics({ commit, rootState, state }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/all-admin`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_TOPICS', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get topics'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get public topics
  async getPublicTopics({ commit, rootState, state }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/public`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_TOPICS', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get public topics'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get topic details
  async getTopicDetails({ commit, rootState, state }, topicId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/${topicId}/details`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_TOPIC_DETAIL', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get topic details'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Create new topic
  async createTopic({ commit, rootState, state }, topicData) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.post(`${state.host}/api/topic/create`, topicData, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.data && response.data.data) {
        commit('ADD_TOPIC', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create topic'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Update topic
  async updateTopic({ commit, rootState, state }, { topicId, topicData }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.put(`${state.host}/api/topic/${topicId}`, topicData, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.data && response.data.data) {
        commit('UPDATE_TOPIC', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update topic'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Delete topic
  async deleteTopic({ commit, rootState, state }, topicId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.delete(`${state.host}/api/topic/${topicId}`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      commit('DELETE_TOPIC', topicId)
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete topic'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get topic subscribers with pagination
  async getTopicSubscribers({ commit, rootState, state }, { topicName, page = 0, size = 20 }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/${topicName}/subscribers`, {
        params: { page, size },
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_TOPIC_SUBSCRIBERS', response.data.data.content || [])
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get topic subscribers'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get all categories
  async getAllCategories({ commit, rootState, state }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/categories`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_CATEGORIES', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get categories'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get categories with statistics (admin)
  async getCategoriesWithStats({ commit, rootState, state }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/categories/stats`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_CATEGORIES', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get categories with stats'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Rename category
  async renameCategory({ commit, rootState, state }, { oldCategory, newCategory }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.put(`${state.host}/api/topic/categories/rename`, null, {
        params: {
          oldCategory,
          newCategory
        },
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to rename category'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Delete category
  async deleteCategory({ commit, rootState, state }, category) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.delete(`${state.host}/api/topic/categories/${encodeURIComponent(category)}`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete category'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Search topics
  async searchTopics({ commit, rootState, state }, keyword) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/search`, {
        params: { keyword },
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_TOPICS', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to search topics'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get topics by category
  async getTopicsByCategory({ commit, rootState, state }, category) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/topic/category/${encodeURIComponent(category)}`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      if (response.data && response.data.data) {
        commit('SET_TOPICS', response.data.data)
      }
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get topics by category'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Auto-subscribe all users to a topic
  async autoSubscribeAllUsers({ commit, rootState, state }, topicName) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.post(
        `${state.host}/api/topic/${topicName}/auto-subscribe-all`, 
        {},
        {
          headers: {
            'Authorization': `Bearer ${rootState.token}`
          }
        }
      )
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to auto-subscribe users'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Sync all users to mandatory topics
  async syncMandatoryTopics({ commit, rootState, state }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.post(
        `${state.host}/api/topic/sync-mandatory-topics`, 
        {},
        {
          headers: {
            'Authorization': `Bearer ${rootState.token}`
          }
        }
      )
      
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to sync mandatory topics'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
